import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import api from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  GraduationCap, CheckCircle2, Clock, ShieldCheck, ArrowLeft, Send, User as UserIcon,
  MapPin, Building2, Briefcase, FileText, Calendar as CalendarIcon, Phone, Mail, KeyRound
} from 'lucide-react';

const PRO_STATUS_OPTIONS = [
  { value: 'salarie', label: 'Salarié en auto-école' },
  { value: 'independant', label: 'Moniteur indépendant' },
  { value: 'formateur_ccs', label: 'Formateur CCS' },
  { value: 'autre', label: 'Autre' }
];

export default function TeacherApplication() {
  const navigate = useNavigate();
  const { dark } = useApp();
  const [form, setForm] = useState({
    first_name: '', last_name: '', birth_date: '', birth_place: '',
    email: '', phone: '', password: '', confirm_password: '',
    address: '', city: '', postal_code: '',
    pro_status: 'salarie', school_name: '', siret: '', years_experience: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState(null);

  const onChange = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const onStatusChange = (v) => setForm(f => ({ ...f, pro_status: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    // Validation client
    if (form.password.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }
    if (form.password !== form.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        birth_date: form.birth_date,
        birth_place: form.birth_place.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        password: form.password,
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        postal_code: form.postal_code.trim() || null,
        pro_status: form.pro_status,
        school_name: form.school_name.trim() || null,
        siret: form.siret.trim() || null,
        years_experience: form.years_experience ? parseInt(form.years_experience, 10) : null,
        message: form.message.trim() || null,
      };
      const res = await api.submitTeacherApplication(payload);
      setApplicationId(res.application_id);
      setSubmitted(true);
      toast.success('Demande envoyée avec succès');
    } catch (err) {
      toast.error(err.message || "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  // --- Écran de confirmation ---
  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto py-6" data-testid="teacher-application-success">
        <div className="mc-card p-8 sm:p-10 text-center relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-60 h-60 rounded-full blur-3xl opacity-20 bg-[#18794e]" />
          <div className="relative">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#18794e]/10 border border-[#18794e]/30 flex items-center justify-center mb-5">
              <CheckCircle2 className="w-8 h-8 text-[#18794e] dark:text-emerald-400" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight mb-3">
              Votre demande a été envoyée !
            </h1>
            <p className="text-sm text-foreground/80 max-w-md mx-auto leading-relaxed">
              Merci pour votre candidature. Notre équipe va examiner votre dossier dans les plus brefs délais.
            </p>

            <div className="mt-6 rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 p-4 text-left max-w-md mx-auto">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-700 dark:text-amber-300" />
                <div className="flex-1">
                  <div className="text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-300 mb-0.5">
                    Délai de réponse
                  </div>
                  <p className="text-xs text-foreground/85 leading-relaxed">
                    Vous recevrez une réponse de notre équipe <strong>sous 48 h ouvrées</strong> par email
                    à l'adresse fournie. Une fois votre compte validé, vous pourrez vous connecter avec
                    l'email et le mot de passe que vous avez choisis.
                  </p>
                </div>
              </div>
            </div>

            {applicationId && (
              <div className="mt-4 text-xs text-muted-foreground font-mono">
                N° de suivi : {applicationId.slice(0, 8).toUpperCase()}
              </div>
            )}

            <div className="flex flex-wrap gap-2 justify-center mt-6">
              <Button
                onClick={() => navigate('/')}
                className="mc-btn-primary h-11 px-5 rounded-full"
                data-testid="teacher-application-home-cta"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Retour à l'accueil
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Formulaire ---
  return (
    <div className="max-w-3xl mx-auto py-4" data-testid="teacher-application-page">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/register')}
        className="gap-1.5 -ml-2 mb-3"
        data-testid="teacher-application-back"
      >
        <ArrowLeft className="w-4 h-4" /> Retour à l'inscription
      </Button>

      <div className="mc-card p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-2">
          <img
            src="/logo-c2r.png"
            alt="CODE 2 ROUES"
            className={`w-11 h-11 rounded-2xl object-cover bg-[#0b1b3a]`}
          />
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))]">
              <GraduationCap className="w-3.5 h-3.5" /> Demande d'accès enseignant
            </div>
            <h1 className="text-xl sm:text-2xl font-black tracking-tight">Rejoindre Code 2 Roues</h1>
            <p className="text-xs text-muted-foreground">Formulaire de candidature moniteur / formateur</p>
          </div>
        </div>

        <div className="mt-4 rounded-xl bg-[hsl(var(--primary-soft))] border border-[hsl(var(--primary))]/20 p-3 text-xs text-foreground/85">
          <div className="flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5 text-[hsl(var(--primary))]" />
            <div>
              <strong>Votre demande sera examinée manuellement</strong> pour garantir la qualité du contenu
              pédagogique. Réponse par email sous 48 h ouvrées.
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="mt-6 space-y-6">
          {/* --- Identité --- */}
          <fieldset className="space-y-3">
            <legend className="text-[11px] font-black uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
              <UserIcon className="w-3.5 h-3.5" /> Identité
            </legend>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="first_name">Prénom *</Label>
                <Input id="first_name" required value={form.first_name} onChange={onChange('first_name')} data-testid="ta-first-name" />
              </div>
              <div>
                <Label htmlFor="last_name">Nom *</Label>
                <Input id="last_name" required value={form.last_name} onChange={onChange('last_name')} data-testid="ta-last-name" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="birth_date" className="flex items-center gap-1.5"><CalendarIcon className="w-3.5 h-3.5" /> Date de naissance *</Label>
                <Input id="birth_date" type="date" required value={form.birth_date} onChange={onChange('birth_date')} data-testid="ta-birth-date" />
              </div>
              <div>
                <Label htmlFor="birth_place" className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Lieu de naissance *</Label>
                <Input id="birth_place" required value={form.birth_place} onChange={onChange('birth_place')} placeholder="Lyon, France" data-testid="ta-birth-place" />
              </div>
            </div>
          </fieldset>

          {/* --- Contact + identifiants --- */}
          <fieldset className="space-y-3">
            <legend className="text-[11px] font-black uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> Contact & identifiants de connexion
            </legend>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" required value={form.email} onChange={onChange('email')} placeholder="vous@example.com" data-testid="ta-email" />
              </div>
              <div>
                <Label htmlFor="phone" className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Téléphone *</Label>
                <Input id="phone" type="tel" required value={form.phone} onChange={onChange('phone')} placeholder="06 12 34 56 78" data-testid="ta-phone" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="password" className="flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Mot de passe *</Label>
                <PasswordInput id="password" required minLength={6} value={form.password} onChange={onChange('password')} placeholder="Minimum 6 caractères" data-testid="ta-password" />
              </div>
              <div>
                <Label htmlFor="confirm_password">Confirmation *</Label>
                <PasswordInput id="confirm_password" required minLength={6} value={form.confirm_password} onChange={onChange('confirm_password')} placeholder="Ressaisir le mot de passe" data-testid="ta-confirm-password" />
              </div>
            </div>
          </fieldset>

          {/* --- Adresse --- */}
          <fieldset className="space-y-3">
            <legend className="text-[11px] font-black uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> Adresse (optionnel)
            </legend>
            <div>
              <Label htmlFor="address">Adresse</Label>
              <Input id="address" value={form.address} onChange={onChange('address')} placeholder="12 rue de la Moto" data-testid="ta-address" />
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="postal_code">Code postal</Label>
                <Input id="postal_code" value={form.postal_code} onChange={onChange('postal_code')} placeholder="69000" data-testid="ta-postal-code" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="city">Ville</Label>
                <Input id="city" value={form.city} onChange={onChange('city')} placeholder="Lyon" data-testid="ta-city" />
              </div>
            </div>
          </fieldset>

          {/* --- Statut pro --- */}
          <fieldset className="space-y-3">
            <legend className="text-[11px] font-black uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
              <Briefcase className="w-3.5 h-3.5" /> Statut professionnel
            </legend>
            <div>
              <Label>Statut *</Label>
              <Select value={form.pro_status} onValueChange={onStatusChange}>
                <SelectTrigger data-testid="ta-pro-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRO_STATUS_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="school_name" className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Nom de l'auto-école / structure</Label>
                <Input id="school_name" value={form.school_name} onChange={onChange('school_name')} placeholder="Auto-école XYZ" data-testid="ta-school-name" />
              </div>
              <div>
                <Label htmlFor="siret">N° SIRET (si indépendant)</Label>
                <Input id="siret" value={form.siret} onChange={onChange('siret')} placeholder="123 456 789 00010" data-testid="ta-siret" />
              </div>
            </div>
            <div>
              <Label htmlFor="years_experience">Années d'expérience</Label>
              <Input id="years_experience" type="number" min="0" max="60" value={form.years_experience} onChange={onChange('years_experience')} placeholder="5" data-testid="ta-years-exp" />
            </div>
          </fieldset>

          {/* --- Message --- */}
          <fieldset className="space-y-3">
            <legend className="text-[11px] font-black uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" /> Message libre (optionnel)
            </legend>
            <Textarea
              id="message"
              value={form.message}
              onChange={onChange('message')}
              rows={4}
              placeholder="Présentez-vous brièvement : votre parcours, vos spécialités (moto, scooter, CCS, etc.), votre motivation à rejoindre la plateforme…"
              data-testid="ta-message"
            />
          </fieldset>

          <div className="flex flex-col sm:flex-row gap-2 items-center justify-end pt-2">
            <Link to="/register" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Annuler et retour
            </Link>
            <Button
              type="submit"
              disabled={loading}
              className="mc-btn-primary h-11 px-6 rounded-full font-black w-full sm:w-auto"
              data-testid="ta-submit-btn"
            >
              <Send className="w-4 h-4 mr-1.5" /> {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
